import { useEffect, useState, useMemo } from "react";
import {
  Search, Filter, Download, Phone, MessageSquare, Loader2, X, Save,
  Calendar as CalIcon, MapPin, Mail, NotebookPen, ChevronDown, BellRing, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import apiClient, { API_BASE } from "./api";
import { BRAND } from "../data/mock";
import ReminderModal from "./ReminderModal";
import NewBookingModal from "./NewBookingModal";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STATUSES = ["new", "pending", "contacted", "confirmed", "completed", "cancelled"];

const STATUS_COLOR = {
  new: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  pending: "bg-[#FC5106]/15 text-[#FC5106] border-[#FC5106]/40",
  contacted: "bg-[#8B73FF]/15 text-[#C9BCFF] border-[#8B73FF]/40",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  completed: "bg-white/10 text-white/70 border-white/20",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

function whatsappReminderUrl(b) {
  const dateStr = (() => {
    try { return format(parseISO(b.date), "PPP"); } catch { return b.date; }
  })();
  const lines = [
    `Hi ${b.name}, this is a friendly reminder from BarknFurs Grooming Studio.`,
    "",
    `Your appointment is scheduled for *${dateStr}* at *${b.time_slot}*.`,
    `Service: ${b.service}`,
    `Location: ${b.location === "Home" ? "Home Grooming (Doorstep)" : "At our studio"}`,
    "",
    "Reply YES to confirm or call us if you need to reschedule.",
    `— BarknFurs ${BRAND.phone}`,
  ];
  const phone = (b.phone || "").replace(/\D/g, "");
  const num = phone.length === 10 ? `91${phone}` : phone;
  return `https://wa.me/${num}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [drawer, setDrawer] = useState(null);
  const [reminderForBooking, setReminderForBooking] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (locationFilter !== "all") params.location = locationFilter;
      if (search) params.search = search;
      const { data } = await apiClient.get("/api/admin/bookings", { params });
      setBookings(data);
    } catch (e) {
      toast.error("Could not load bookings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, locationFilter]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("bf_token");
      const res = await fetch(`${API_BASE}/admin/bookings/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `barknfurs-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Could not download CSV");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Bookings</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
            All bookings & leads
          </h1>
          <p className="text-white/50 text-sm mt-1">{bookings.length} bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNew(true)}
            data-testid="bookings-new-btn"
            className="btn-primary !py-2.5 !px-4 !text-sm"
          >
            <Plus className="w-4 h-4" /> New booking
          </button>
          <button onClick={exportCsv} data-testid="bookings-export-csv" className="btn-secondary !py-2.5 !px-4 !text-sm">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-4 grid md:grid-cols-12 gap-3">
        <div className="md:col-span-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="bookings-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, phone, breed, service…"
            className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none"
          />
        </div>
        <div className="md:col-span-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="bookings-status-filter" className="bg-black/50 border-white/10 text-sm text-white">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <Select value={locationFilter} onValueChange={setLocationFilter}>
            <SelectTrigger data-testid="bookings-location-filter" className="bg-black/50 border-white/10 text-sm text-white">
              <SelectValue placeholder="All locations" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 text-white">
              <SelectItem value="all">All locations</SelectItem>
              <SelectItem value="Studio">Studio</SelectItem>
              <SelectItem value="Home">Home</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" />
          </div>
        ) : bookings.length === 0 ? (
          <p className="text-white/40 text-center py-16 text-sm">No bookings match your filters.</p>
        ) : (
          <>
            {/* Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="bookings-table">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-white/50 bg-black/40">
                    <th className="px-5 py-3 font-medium">Customer</th>
                    <th className="px-3 py-3 font-medium">Pet</th>
                    <th className="px-3 py-3 font-medium">Service</th>
                    <th className="px-3 py-3 font-medium">Schedule</th>
                    <th className="px-3 py-3 font-medium">Loc</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr
                      key={b.id}
                      className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setDrawer(b)}
                      data-testid={`booking-row-${b.id}`}
                    >
                      <td className="px-5 py-3 align-top">
                        <p className="text-white font-semibold">{b.name}</p>
                        <p className="text-white/50 text-xs">{b.phone}</p>
                      </td>
                      <td className="px-3 py-3 align-top text-white/80 text-xs">
                        {b.pet_name || "—"}<br />
                        <span className="text-white/40">{b.pet_type}{b.pet_breed ? ` · ${b.pet_breed}` : ""}</span>
                      </td>
                      <td className="px-3 py-3 align-top text-white/80 text-xs">
                        {b.service}
                        {b.offer_code && (
                          <span data-testid={`booking-offer-${b.id}`} title={b.offer_title} className="mt-1 block w-fit font-mono text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFB547]/15 text-[#FFB547] border border-[#FFB547]/40">
                            {b.offer_code}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top text-white/80 text-xs whitespace-nowrap">
                        {b.date}<br />
                        <span className="text-white/50">{b.time_slot}</span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                          b.location === "Home" ? "bg-[#8B73FF]/15 text-[#C9BCFF] border-[#8B73FF]/40" : "bg-white/5 text-white/60 border-white/10"
                        }`}>
                          {b.location || "Studio"}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_COLOR[b.status] || STATUS_COLOR.new}`}>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-top text-right whitespace-nowrap">
                        <a
                          href={`tel:${b.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 mr-1.5"
                          aria-label="Call"
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                        <a
                          href={whatsappReminderUrl(b)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25"
                          aria-label="WhatsApp reminder"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-3">
              {bookings.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setDrawer(b)}
                  className="w-full text-left rounded-xl border border-white/10 bg-black/30 p-4 hover:border-white/20"
                  data-testid={`booking-card-${b.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-sm truncate">{b.name}</p>
                      <p className="text-white/50 text-xs">{b.phone}</p>
                    </div>
                    <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${STATUS_COLOR[b.status] || STATUS_COLOR.new}`}>
                      {b.status}
                    </span>
                  </div>
                  <p className="text-white/70 text-xs">{b.service}{b.offer_code && <span className="ml-1 font-mono text-[#FFB547]">· {b.offer_code}</span>}</p>
                  <p className="text-white/50 text-xs mt-1">{b.date} · {b.time_slot} · {b.location || "Studio"}</p>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Drawer */}
      {drawer && (
        <BookingDrawer
          booking={drawer}
          onClose={() => setDrawer(null)}
          onSaved={(updated) => {
            setBookings((arr) => arr.map((b) => (b.id === updated.id ? updated : b)));
            setDrawer(updated);
          }}
          onSetReminder={(b) => {
            setReminderForBooking({
              customer_name: b.name,
              customer_phone: b.phone,
              customer_email: b.email,
              service: b.service,
              pet_name: b.pet_name,
              pet_breed: b.pet_breed,
              notes: `Follow up on booking for ${b.date} ${b.time_slot} (${b.location || "Studio"}). No response from customer.`,
            });
            setDrawer(null);
          }}
        />
      )}

      {reminderForBooking && (
        <ReminderModal
          open
          defaults={reminderForBooking}
          onClose={() => setReminderForBooking(null)}
          onCreated={() => toast.success("Reminder added — view it in Reminders tab")}
        />
      )}

      <NewBookingModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={(b) => setBookings((arr) => [b, ...arr])}
      />
    </div>
  );
}

function BookingDrawer({ booking, onClose, onSaved, onSetReminder }) {
  const [status, setStatus] = useState(booking.status);
  const [notes, setNotes] = useState(booking.internal_notes || "");
  const [saving, setSaving] = useState(false);

  const dirty = status !== booking.status || (notes || "") !== (booking.internal_notes || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.patch(`/api/admin/bookings/${booking.id}`, {
        status,
        internal_notes: notes,
      });
      toast.success("Saved");
      onSaved(data);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="booking-drawer"
    >
      <div
        className="w-full max-w-lg h-full bg-[#0d0d0d] border-l border-white/10 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0d0d0d]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/50">Booking</p>
            <h2 className="font-heading text-lg font-bold text-white truncate max-w-[260px]">{booking.name}</h2>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Phone" icon={Phone} value={
            <a href={`tel:${booking.phone}`} className="text-[#C9BCFF] hover:underline">{booking.phone}</a>
          } />
          {booking.email && (
            <Field label="Email" icon={Mail} value={
              <a href={`mailto:${booking.email}`} className="text-white/80 hover:text-white">{booking.email}</a>
            } />
          )}
          <Field label="Pet" value={
            <span className="text-white/80 text-sm">
              {booking.pet_name || "—"} · {booking.pet_type}{booking.pet_breed ? ` · ${booking.pet_breed}` : ""}
            </span>
          } />
          <Field label="Service" value={<span className="text-white/80 text-sm">{booking.service}</span>} />
          <Field label="Schedule" icon={CalIcon} value={
            <span className="text-white/80 text-sm">{booking.date} · {booking.time_slot}</span>
          } />
          <Field label="Location" icon={MapPin} value={
            <span className="text-white/80 text-sm">
              {booking.location === "Home" ? "Home Grooming (Doorstep)" : "At Studio"}
            </span>
          } />
          {booking.message && (
            <Field label="Customer note" value={<p className="text-white/70 text-sm whitespace-pre-line">{booking.message}</p>} />
          )}

          <div className="pt-2 border-t border-white/10" />

          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="drawer-status-select" className="bg-black/50 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Internal notes</label>
            <textarea
              data-testid="drawer-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Private notes about this lead — only visible to staff."
              className="w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none resize-none"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href={`tel:${booking.phone}`}
              data-testid="drawer-call"
              className="btn-secondary !py-2.5 !px-4 !text-sm"
            >
              <Phone className="w-4 h-4" /> Call
            </a>
            <a
              href={whatsappReminderUrl(booking)}
              target="_blank"
              rel="noreferrer"
              data-testid="drawer-remind"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366]/15 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25 rounded-full px-4 py-2.5 text-sm font-semibold"
            >
              <MessageSquare className="w-4 h-4" /> Send reminder
            </a>
            <button
              onClick={() => onSetReminder && onSetReminder(booking)}
              data-testid="drawer-set-reminder"
              className="inline-flex items-center justify-center gap-2 bg-[#FC5106]/15 border border-[#FC5106]/40 text-[#FC5106] hover:bg-[#FC5106]/25 rounded-full px-4 py-2.5 text-sm font-semibold"
            >
              <BellRing className="w-4 h-4" /> Set follow-up reminder
            </button>
          </div>

          <button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="drawer-save"
            className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-white/50 mb-1 flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </p>
      <div>{value}</div>
    </div>
  );
}
